import { useEffect, useState } from "react";
import { apiClient, formatApiError, uploadFile, resolveUrl } from "@/lib/api";
import { youtubeEmbed } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Loader2, Upload } from "lucide-react";
import { PageHeader, Card, Field, inputCls, btnPrimary, btnGhost, btnDanger } from "@/components/admin/ui";

const empty = { title: "", description: "", youtube_url: "", before_image: "", after_image: "" };

export default function VideosAdminPage() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchItems = () => {
        setLoading(true);
        apiClient.get("/videos").then((r) => setItems(r.data)).finally(() => setLoading(false));
    };
    useEffect(fetchItems, []);

    const del = async (id) => {
        if (!window.confirm("Delete this video?")) return;
        await apiClient.delete(`/videos/${id}`);
        toast.success("Deleted");
        fetchItems();
    };

    return (
        <>
            <PageHeader
                title="Videos"
                subtitle="Add YouTube videos with optional before/after images."
                action={<button onClick={() => setEditing({ ...empty })} data-testid="add-video-btn" className={btnPrimary}><Plus size={16} /> Add video</button>}
            />

            {loading ? <Loader2 className="animate-spin text-walnut" /> : (
                <div className="grid md:grid-cols-2 gap-6">
                    {items.map((v) => (
                        <Card key={v.id} className="overflow-hidden" data-testid={`video-admin-${v.id}`}>
                            <div className="aspect-video bg-sand dark:bg-stone-800">
                                <iframe src={youtubeEmbed(v.youtube_url)} title={v.title} className="w-full h-full" frameBorder="0" allowFullScreen />
                            </div>
                            <div className="p-4">
                                <div className="font-serif text-xl">{v.title}</div>
                                <div className="text-xs text-stone-muted dark:text-stone-400 mt-1 line-clamp-2">{v.description}</div>
                                <div className="flex gap-2 mt-3">
                                    <button onClick={() => setEditing({ ...v })} className={btnGhost}><Edit3 size={14} /> Edit</button>
                                    <button onClick={() => del(v.id)} data-testid={`video-delete-${v.id}`} className={btnDanger}><Trash2 size={14} /></button>
                                </div>
                            </div>
                        </Card>
                    ))}
                    {!items.length && <div className="col-span-full text-stone-muted py-12">No videos.</div>}
                </div>
            )}

            {editing && <VideoModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchItems(); }} />}
        </>
    );
}

function VideoModal({ initial, onClose, onSaved }) {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial.id;

    const upload = async (e, key) => {
        const f = e.target.files?.[0];
        if (!f) return;
        try {
            const r = await uploadFile(f);
            setForm({ ...form, [key]: r.url });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { title: form.title, description: form.description, youtube_url: form.youtube_url, before_image: form.before_image || null, after_image: form.after_image || null };
            if (isEdit) await apiClient.patch(`/videos/${form.id}`, payload);
            else await apiClient.post("/videos", payload);
            toast.success(isEdit ? "Updated" : "Added");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-auto">
            <form onSubmit={submit} className="bg-cream dark:bg-[#171311] w-full max-w-2xl p-6 md:p-8 my-6" data-testid="video-form">
                <div className="flex justify-between items-start mb-6">
                    <h2 className="font-serif text-2xl">{isEdit ? "Edit video" : "Add video"}</h2>
                    <button type="button" onClick={onClose}><X /></button>
                </div>
                <div className="space-y-4">
                    <Field label="Title" required><input data-testid="video-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
                    <Field label="YouTube URL" required><input data-testid="video-url" required className={inputCls} value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." /></Field>
                    <Field label="Description"><textarea rows={3} className={inputCls + " resize-none"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
                    {["before_image", "after_image"].map((k) => (
                        <Field key={k} label={`${k === "before_image" ? "Before" : "After"} image (optional)`}>
                            <div className="flex gap-3 items-start">
                                {form[k] && <img src={resolveUrl(form[k])} alt="" className="w-20 h-20 object-cover bg-sand" />}
                                <div className="flex-1 space-y-2">
                                    <input className={inputCls} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                                    <label className={btnGhost + " cursor-pointer"}><Upload size={14} /> Upload<input type="file" accept="image/*" onChange={(e) => upload(e, k)} className="hidden" /></label>
                                </div>
                            </div>
                        </Field>
                    ))}
                </div>
                <div className="flex gap-3 mt-6">
                    <button disabled={saving} className={btnPrimary} data-testid="video-save">{isEdit ? "Save" : "Add"}</button>
                    <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
                </div>
            </form>
        </div>
    );
}
